import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import ConsultationModal from './ConsultationModal';
import { useLanguage } from '@/contexts/LanguageContext';
import EditableText from './cms/EditableText';

const ContactSection = () => {
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const { t } = useLanguage();
  
  const contactInfo = [
    {
      icon: Mail,
      label: t('contact.email'),
      valueKey: 'contact.emailValue',
      href: "mailto:info@commodit.hu"
    },
    {
      icon: Phone,
      label: t('contact.phone'),
      valueKey: 'contact.phoneValue',
      href: "tel:+36301176526"
    },
    {
      icon: MapPin,
      label: t('contact.location'),
      valueKey: 'contact.locationValue',
    },
    {
      icon: Clock,
      label: t('contact.responseTime'),
      valueKey: 'contact.lessThan24Hours',
    }
  ];

  return (
    <section id="contact" className="py-24 px-4 relative">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-hero opacity-50"></div>
      
      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-display font-bold mb-6">
            <EditableText k="contact.readyToGetStarted" /> <EditableText k="contact.getStarted" className="gradient-text" />?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            <EditableText k="contact.discussProject" />
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-display font-bold mb-6 text-foreground">
                <EditableText k="contact.letsConnect" />
              </h3>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                <EditableText k="contact.readyToTakeNext" />
              </p>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => {
                const Component = info.href ? 'a' : 'div';
                return (
                  <Component
                    key={info.label}
                    {...(info.href ? { href: info.href } : {})}
                    className="flex items-center gap-4 p-4 rounded-2xl card-gradient border border-border/50 hover:border-primary/50 transition-smooth hover:glow-primary group"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-smooth">
                      <info.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">{info.label}</div>
                      <div className="text-lg font-medium text-foreground">
                        <EditableText k={info.valueKey} />
                      </div>
                    </div>
                  </Component>
                );
              })}
            </div>

            {/* Languages */}
            <div className="p-6 rounded-2xl card-gradient border border-border/50">
              <h4 className="text-xl font-semibold text-foreground mb-4"><EditableText k="contact.languages" /></h4>
              <div className="flex gap-4">
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"><EditableText k="contact.english" /></span>
                <span className="px-3 py-1 bg-accent/10 text-accent rounded-full text-sm"><EditableText k="contact.magyar" /></span>
              </div>
            </div>
          </div>

          {/* Right Column - CTA */}
          <div className="flex flex-col justify-center">
            <div className="card-gradient border border-border/50 rounded-3xl p-8 md:p-12 text-center glow-secondary">
              <h3 className="text-3xl font-display font-bold mb-6 text-foreground">
                <EditableText k="contact.freeConsultation" />
              </h3>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                <EditableText k="contact.bookDiscovery" />
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-center gap-3 text-foreground">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <EditableText k="contact.projectScope" />
                </div>
                <div className="flex items-center justify-center gap-3 text-foreground">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <EditableText k="contact.techRecommendations" />
                </div>
                <div className="flex items-center justify-center gap-3 text-foreground">
                  <div className="w-2 h-2 bg-secondary rounded-full"></div>
                  <EditableText k="contact.timelineBudget" />
                </div>
              </div>

              <Button 
                size="lg" 
                className="w-full gradient-primary text-sm sm:text-lg py-6 rounded-full glow-primary pulse-glow transition-spring hover:scale-105"
                onClick={() => setIsConsultationOpen(true)}
              >
                <span className="text-center px-2">
                  <EditableText k="contact.scheduleConsultation" />
                </span>
              </Button>

              <p className="text-sm text-muted-foreground mt-4">
                <EditableText k="contact.usuallyResponds" />
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Consultation Modal */}
      <ConsultationModal 
        open={isConsultationOpen} 
        onOpenChange={setIsConsultationOpen} 
      />
    </section>
  );
};

export default ContactSection;